import type {
  BaseNodeData,
  IMediaPaint,
  Paint,
} from "@shared/common/node/type";
import { traversePartial } from "@shared/common/node/util";
import { derived, writable } from "svelte/store";
import type { NodeStore } from "./node";

// TODO: createStore 안에 옮겨야 하지만, 중요도는 낮음. 팝업(iframe)에서만 사용되는 로딩 관련 변수이므로 당장 포함시킬 필요는 없음.
const totalImageCount = writable(0);
const loadedImageCount = writable(0);

export const startImageLoading = () => {
  totalImageCount.update((n) => n + 1);
};

export const finishImageLoading = () => {
  loadedImageCount.update((n) => n + 1);
};

const getImageUrlsFromFills = (paints: Paint[]): string[] => {
  return paints
    .filter(
      (paint): paint is IMediaPaint =>
        paint.type === "IMAGE" && paint.visible !== false // visible이 undefined 일 경우에는 true로 간주
    )
    .map((imagePaint) => imagePaint.path)
    .filter((maybePath): maybePath is string => typeof maybePath === "string");
};

const getLoadingImageUrls = (node: BaseNodeData): string[] => {
  if (node.type === "FRAME" && node.fills) {
    const fromFills = getImageUrlsFromFills(node.fills);
    const fromEntranceImage = node.entranceImage?.image
      ? getImageUrlsFromFills([node.entranceImage.image])
      : [];
    return [...fromFills, ...fromEntranceImage];
  } else if (node.type === "TEXT" && node.backgrounds) {
    return getImageUrlsFromFills(node.backgrounds);
  } else if (node.type === "IMAGE" && node.fills) {
    return getImageUrlsFromFills(node.fills);
  } else {
    return [];
  }
};

const createStore = (nodeStore: NodeStore) => {
  const imageUrls = derived(nodeStore.state.nodeData, ($nodeData) => {
    if (!$nodeData) {
      return [];
    }

    const result: string[] = [];

    traversePartial({
      node: $nodeData,
      handler: (node) => {
        result.push(...getLoadingImageUrls(node));
      },
    });

    return result;
  });

  const noImage = derived(
    [imageUrls, nodeStore.state.nodeData],
    ([$imageUrls, $nodeData]) => $nodeData && $imageUrls.length === 0
  );

  const imageLoadingStarted = derived(totalImageCount, ($totalImageCount) => {
    return $totalImageCount > 0;
  });

  const finishedLoadingImage = derived(
    [totalImageCount, loadedImageCount, imageLoadingStarted],
    ([$totalImageCount, $loadedImageCount, $imageLoadingStarted]) => {
      return $imageLoadingStarted && $loadedImageCount === $totalImageCount;
    }
  );

  const state = {
    totalImageCount,
    loadedImageCount,
    imageUrls,
    noImage,
    imageLoadingStarted,
    finishedLoadingImage,
  };
  return {
    state,
    reset: () => {
      totalImageCount.set(0);
      loadedImageCount.set(0);
    },
  };
};

export default createStore;
